/**
 * What a site runs on, and where that is administered (ADR-0031).
 *
 * A site the panel runs is WordPress by construction. A watched one is
 * recognised from its pages, reported by its server's agent, or said by an
 * operator; `adminPath` is where a platform's own sign-in lives on the site's
 * host, null where it lives elsewhere (Tilda, Wix, a separate CMS console).
 *
 * `serves` says what the site is on a server, which is what the panel can do
 * with it — never the platform's name: `files` — its files are the whole site
 * (static pages, a generator's output), copied as they are; `cms` — a CMS or
 * an application with a database behind it; `service` — hosted by someone
 * else, with nothing on a server of ours.
 */
export const PLATFORMS = {
  wordpress: { label: 'WordPress', adminPath: '/wp-admin/', serves: 'cms' },
  convertstudio: { label: 'ConvertStudio', adminPath: null, serves: 'files' },
  joomla: { label: 'Joomla', adminPath: '/administrator/', serves: 'cms' },
  drupal: { label: 'Drupal', adminPath: '/user/login', serves: 'cms' },
  bitrix: { label: '1С-Битрикс', adminPath: '/bitrix/admin/', serves: 'cms' },
  modx: { label: 'MODX', adminPath: '/manager/', serves: 'cms' },
  opencart: { label: 'OpenCart', adminPath: '/admin/', serves: 'cms' },
  ghost: { label: 'Ghost', adminPath: '/ghost/', serves: 'cms' },
  shopify: { label: 'Shopify', adminPath: '/admin', serves: 'service' },
  tilda: { label: 'Tilda', adminPath: null, serves: 'service' },
  wix: { label: 'Wix', adminPath: null, serves: 'service' },
  webflow: { label: 'Webflow', adminPath: null, serves: 'service' },
  static: { label: 'Статический сайт', adminPath: null, serves: 'files' },
  custom: { label: 'Своя CMS', adminPath: null, serves: 'cms' },
} as const satisfies Record<string, { label: string; adminPath: string | null; serves: 'files' | 'cms' | 'service' }>;

export type PlatformName = keyof typeof PLATFORMS;

export const PLATFORM_NAMES = Object.keys(PLATFORMS) as [PlatformName, ...PlatformName[]];

export function isPlatform(value: unknown): value is PlatformName {
  return typeof value === 'string' && Object.hasOwn(PLATFORMS, value);
}

/**
 * Whether a site may be copied as its files alone (ADR-0038): a platform
 * known to be files, or one not known yet — then the server's agent decides
 * from what it finds, and refuses a site with a database behind it.
 */
export function copiedAsFiles(platform: string | null): boolean {
  if (platform === null || !isPlatform(platform)) return true;
  return PLATFORMS[platform].serves === 'files';
}

/** A platform's name as people say it; an unknown one as it was stored. */
export function platformLabel(name: string | null): string | null {
  if (name === null) return null;
  return isPlatform(name) ? PLATFORMS[name].label : name;
}

/**
 * Whether the panel knows the site's sign-in form by itself (ADR-0035):
 * WordPress's, the one form that is the same on every site. Any other CMS
 * signs in with the form its credential describes (ADR-0042, `signsInWith`).
 */
export function signsInFromPanel(site: { readonly engine: string; readonly platform: string | null }): boolean {
  return site.platform === 'wordpress' || site.engine === 'wordpress';
}

/** How a site's platform became known. */
export const PLATFORM_SOURCES = {
  http: 'по страницам сайта',
  agent: 'агентом на сервере',
  operator: 'указано вручную',
} as const;

export type PlatformSource = keyof typeof PLATFORM_SOURCES;
